import AddAPhoto from "@mui/icons-material/AddAPhoto";
import Close from "@mui/icons-material/Close";
import QuestionMark from "@mui/icons-material/QuestionMark";
import { Box, CircularProgress, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import { useEffect } from "react";
import { useRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../Contexts/AuthProvider";
import { useCacheContext } from "../Contexts/FileCache";
import useActivityLogger from "../Hooks/useActivityLogger";
import User from "../interfaces/User";
import DeleteAccountPanel from "./DeleteAccountPanelTailwind";
import styles from "./Profile.module.css";
import ProfileRecorder from "./ProfileRecorder";

interface Props {
  userInfo: User,
  onClose: () => void;
}

const ProfileEditor = ({ userInfo, onClose }: Props) => {
  const { updateCredential, hasPrivilege, reloadUser } = useAuthContext();

  const { uncacheFile } = useCacheContext();

  const navigate = useNavigate();
  const postActivity = useActivityLogger();

  const dirtyStatesRef = useRef<Set<string>>(new Set());

  const [modalState, setModalState] = useState<'email' | 'password' | 'delete' | null>(null);

  const [name, setName] = useState(userInfo.name);
  const [handle, setHandle] = useState(userInfo.handle);
  const [introUrl, setIntroUrl] = useState(userInfo.intro ? `/api/users/intro/${userInfo.intro}` : null);

  const [email, setEmail] = useState(userInfo.email);

  const [newPassword, setNewPassword] = useState('');
  const [password, setPassword] = useState('');

  const [nameVisibility, setNameVisibility] = useState(userInfo.nameVisibility);
  const [photoVisibility, setPhotoVisibility] = useState(userInfo.photoVisibility);
  const [introVisibility, setIntroVisibility] = useState(userInfo.introVisibility);

  const [selectedImage, setSelectedImage] = useState(`/api/users/photo/${userInfo.photo}?timestamp=${Date.now()}`);

  const [loading, setLoading] = useState(false);

  const [submitState, setSubmitState] = useState<'' | 'account' | 'metadata'>('');

  const imageInput = useRef<HTMLInputElement | null>(null);
  const imageEditor = useRef<AvatarEditor | null>(null);
  const contentDiv = useRef<HTMLDivElement | null>(null);

  const emailInputRef = useRef<HTMLInputElement | null>(null);

  let defaultLocation = userInfo.location;

  if (userInfo.classes && userInfo.classes.length > 0) {
    defaultLocation = userInfo.classes[0].school.country;
  }

  const [location, setLocation] = useState(defaultLocation ?? '');

  const setDirtyField = (field: string) => {
    dirtyStatesRef.current.add(field);
  }

  const openImagePicker = () => {
    imageInput.current?.click();
  }

  const handleImageEdited = (event: Event) => {
    event.preventDefault();

    if (!contentDiv.current) return;
    contentDiv.current.style.overflow = 'hidden';
  }

  const handleImageEditingEnded = () => {
    if (!contentDiv.current) return;
    contentDiv.current.style.overflow = 'auto';
  }

  const handleImageChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const file = event.target.files[0];
    console.log('uploaded', file);

    setSelectedImage(URL.createObjectURL(file));

    setDirtyField('photo');
  }

  const handleRecorded = (url: string) => {
    setIntroUrl(url);

    setDirtyField('intro');
  }

  const handleDeleteAccount = () => {
    if (hasPrivilege('teacher')) {
      alert('Kindly reach out to your Vactors vendor if you wish to close your school account.');
    }
    else if (hasPrivilege('student')) {
      alert('Need to close your Vactors account? Please ask your teacher for help.');
    }

    else setModalState('delete');
  }

  useEffect(() => {
    if (submitState === 'account') {
      updateAccount()
        .then(res => {
          setSubmitState('metadata');
        })
        .catch((error: any) => {
          setSubmitState('');
          setLoading(false);

          if (error.response) {
            alert(error.response.data);
          } else {
            alert(error.message);
          }
        });
    }
    else if (submitState === 'metadata') {
      updateProfile()
        .then(res => {
          setSubmitState('');
          setLoading(false);

          dirtyStatesRef.current.clear();
          alert('Account updated successfully.');

          onClose();
        })
        .catch((error: any) => {
          setSubmitState('');
          setLoading(false);

          if (error.response) {
            alert(error.response.data);
          } else {
            alert(error.message);
          }
        });
    }
  }, [submitState]);

  const save = async () => {
    if (dirtyStatesRef.current.has('email')) {
      if (!emailInputRef.current?.reportValidity()) {
        alert('Please enter a valid email address.');
        return;
      }
    }

    setLoading(true);

    setSubmitState('account');

    console.log('dirty states', dirtyStatesRef.current);
  }

  const updateProfile = async () => {
    const formData = new FormData();

    const dirtyStates = dirtyStatesRef.current;

    if (dirtyStates.size === 0) return;

    if (dirtyStates.has('name')) {
      formData.append('name', name);
    }

    if (dirtyStates.has('handle')) {
      formData.append('handle', handle);
    }

    if (dirtyStates.has('photo') && imageEditor.current) {
      const editorCanvas = imageEditor.current.getImage();

      const scaleCanvas = document.createElement('canvas');
      scaleCanvas.width = 512;
      scaleCanvas.height = 512;
      const ctx = scaleCanvas.getContext("2d");
      ctx?.drawImage(editorCanvas, 0, 0, 512, 512);

      const canvasData = scaleCanvas.toDataURL();

      let blob = await fetch(canvasData).then(r => r.blob());
      formData.append('photo', blob);
    }

    if (dirtyStates.has('intro') && introUrl) {
      let blob = await fetch(introUrl).then(r => r.blob());
      formData.append('intro', blob);
    }

    if (dirtyStates.has('location')) {
      formData.append('location', location);
    }

    if (dirtyStates.has('nameVisibility')) {
      formData.append('nameVisibility', nameVisibility);
    }
    if (dirtyStates.has('photoVisibility')) {
      formData.append('photoVisibility', photoVisibility);
    }
    if (dirtyStates.has('introVisibility')) {
      formData.append('introVisibility', introVisibility);
    }

    uncacheFile(`/api/users/photo/${userInfo.photo}`);
    await axios.post(`/api/users/profile`, formData);

    //    await reloadUser();
    //postActivity('update_profile', {});
  }

  const updateAccount = async () => {
    const parameters: any = {};

    if (dirtyStatesRef.current.has('email'))
      parameters.newEmail = email;

    if (dirtyStatesRef.current.has('password')) {
      parameters.newPassword = newPassword;
      parameters.password = password;
    }

    if (Object.keys(parameters).length === 0) return;

    const res = await axios.post<{ token: string, user: User }>('/api/users/credential', parameters);

    await updateCredential(res.data.user, res.data.token);

    dirtyStatesRef.current.delete('email');
    dirtyStatesRef.current.delete('password');

    setPassword('');
    setNewPassword('');
  }

  const handleUserDeleted = () => {
    setModalState(null);
    navigate('/', { replace: true });
  }

  const handleCloseModal = () => setModalState(null);

  /*
      <div className={styles["section"]}>
        <label htmlFor="customLink">Custom Portfolio Link</label>

        <TextField fullWidth variant="outlined"
          disabled={userInfo.role.privileges.length === 0 || (userInfo.classes ?? []).length > 0}
          InputProps={{
            startAdornment: <InputAdornment position="start" sx={{ px: '0px' }}>{`${window.location.hostname}/members/`}</InputAdornment>,
            inputProps: {
              style: { borderStyle: 'none', paddingLeft: '0px' }
            }
          }}
          value={handle} onChange={(event) => {
            setHandle(event.target.value.replace(' ', ''));

            setDirtyField('handle');
          }} />
      </div>
  */
  return (
    <div className={styles["profile-section"]}>
      <IconButton
        sx={{ position: 'absolute', right: '5px', top: '5px' }}
        onClick={onClose}>
        <Close />
      </IconButton>
      <div className={styles["section"]} style={{ padding: '0' }}>
        <div className={styles["account-type"]}>
          <span><strong>Membership Type:</strong> {userInfo.role.name}</span>
        </div>
      </div>

      <div className={styles["section"]}>
        <div className={styles["profile-header"]}>
          <div className={styles["profile-photo-container"]}>
            <Box sx={{ position: 'relative', width: '120px', aspectRatio: '1/1' }}>
              <AvatarEditor
                ref={imageEditor}
                style={{ width: '100%', height: '100%' }}
                image={selectedImage}
                border={0}
                borderRadius={999}
                backgroundColor='red'
                color={[255, 255, 255, 255]} // RGBA
                scale={1}
                onMouseMove={handleImageEdited}
                onMouseUp={handleImageEditingEnded}
              />

              <Box sx={{ position: 'absolute', top: '0', bottom: '0', left: '0', right: '0', border: '2px solid #ccc', borderRadius: '60px', pointerEvents: 'none' }}>

              </Box>

              <IconButton
                aria-label="menu"
                sx={{ position: 'absolute', right: '0px', bottom: '0px', color: 'white', background: 'gray' }}
                onClick={openImagePicker}
              >
                <AddAPhoto sx={{}} />
              </IconButton>

              <input type='file' name='image' accept="image/*" ref={imageInput} onChange={handleImageChanged} style={{ display: 'none' }} />
            </Box>

            <label className={styles["photo-toggle"]}>
              <input type="checkbox"
                checked={photoVisibility === 'public'}
                onChange={e => {
                  setPhotoVisibility(e.target.checked ? 'public' : 'protected');
                  setDirtyField('photoVisibility');
                }} /> Show profile photo to everyone
              <Tooltip placement='top'
                title={
                  <Typography>Untick to make it visible only to my Vactors friends.</Typography>
                }
                enterTouchDelay={0}
              >
                <IconButton disableRipple sx={{ backgroundColor: '#4CA3FF', p: '0px', width: '15px', height: '15px' }}>
                  <QuestionMark sx={{ fontSize: '8pt', color: 'white' }} />
                </IconButton>
              </Tooltip>
            </label>
          </div>

          <div className={styles["profile-info"]}>
            <div className={styles["form-group"]}>
              <label htmlFor="name">Name</label>
              <input type="text" id="name"
                value={name} onChange={(event) => {
                  setName(event.target.value);

                  setDirtyField('name');
                }} />
              <label className={styles["toggle-line"]}>
                <input type="checkbox"
                  checked={nameVisibility === 'public'}
                  onChange={e => {
                    setNameVisibility(e.target.checked ? 'public' : 'protected');
                    setDirtyField('nameVisibility');
                  }}
                /> Show full name to everyone
                <Tooltip placement='top'
                  title={
                    <Typography>Untick to make it visible only to my Vactors friends.</Typography>
                  }
                  enterTouchDelay={0}
                >
                  <IconButton disableRipple sx={{ backgroundColor: '#4CA3FF', p: '0px', width: '15px', height: '15px' }}>
                    <QuestionMark sx={{ fontSize: '8pt', color: 'white' }} />
                  </IconButton>
                </Tooltip>
              </label>
            </div>

            <div className={styles["form-group"]} style={{}}>
              <label htmlFor="location">Location</label>
              {userInfo.classes && userInfo.classes.length > 0 ?
                <span>{location}</span>
                :

                <input className={styles["input"]} id="location"
                  value={location} onChange={(event) => {
                    setLocation(event.target.value);

                    setDirtyField('location');
                  }} />
              }
            </div>

            {userInfo.classes && userInfo.classes.length > 0 &&
              <>
                <div className={styles["form-group"]}>
                  <label htmlFor="location">School</label>
                  <span>{userInfo.classes[0].school.name}</span>
                </div>
                <div className={styles["form-group"]}>
                  <label htmlFor="location">Class</label>
                  <span>{userInfo.classes.map(x => x.name).join(', ')}</span>
                </div>
              </>
            }
          </div>
        </div>
      </div>

      <div className={styles["section"]}>
        <label htmlFor="audioIntro">Intro Audio</label>

        <Typography fontSize='10pt' sx={{ marginTop: '0px' }}>Record a short audio to introduce yourself</Typography>
        <ProfileRecorder
          sx={{ my: 2 }}
          playbackUrl={introUrl}
          onRecordingCompleted={handleRecorded}
        />
      </div>


      <div className={styles["section"]}>
        <input type="text" style={{ visibility: 'hidden', height: 0, width: 0, position: 'absolute' }} />
        <input type="password" style={{ visibility: 'hidden', height: 0, width: 0, position: 'absolute' }} />
        <label htmlFor="email">Email</label>

        <input className={styles["input"]} type="email" autoComplete='poggers' style={{ marginBottom: '16px' }}
          ref={emailInputRef}
          value={email}
          onChange={e => {
            setEmail(e.target.value);
            setDirtyField('email');
          }} />
        <label htmlFor="password">Change Password</label>
        <input className={styles["input"]} type="password" autoComplete='new-password' placeholder="Enter current password"
          value={password}
          onChange={e => {
            setPassword(e.target.value);
            setDirtyField('password');
          }} />
        < br />
        < br />
        <input className={styles["input"]} type="password" autoComplete='new-password' placeholder="Enter new password"
          value={newPassword}
          onChange={e => {
            setNewPassword(e.target.value);
            setDirtyField('password');
          }} />
      </div>

      <div className={styles["action-buttons"]}>
        <button className={`${styles["btn"]} ${styles["btn-secondary"]}`}
          onClick={onClose}
        >Cancel</button>
        <button
          onClick={save}
          disabled={dirtyStatesRef.current.size === 0}
          className={`${styles["btn"]} ${styles["btn-primary"]}`}>Save Changes</button>
      </div>


      <div className={styles["danger"]} style={{ marginTop: '24px' }}>
        <h2 id="danger">Danger Zone</h2>
        <p className={styles["note"]}>Closing your account is permanent and cannot be undone.</p>
        <button className={`${styles["btn-danger"]}`} type="button" onClick={handleDeleteAccount}>Close my account</button>
      </div>

      {loading &&
        <Stack alignItems='center' justifyContent='center' sx={{ position: 'fixed', top: '0', bottom: '0', left: '0', right: '0', background: '#bbb6' }}>
          <CircularProgress />
        </Stack>

      }

      {modalState === 'delete' &&
        <DeleteAccountPanel
          onDeleted={handleUserDeleted}
          onClose={handleCloseModal} />
      }
    </div>
  );
}

export default ProfileEditor;